import { useCountByStatus } from "@/api/audiovisual/audiovisual";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import { PagingParameters } from "@/api/dtos";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { generateTable } from "@/config/dashboard/general";
import {
  audiovisualColumns,
  audiovisualColumnsVisibility,
} from "@/config/dashboard/audiovisual";
import { Audiovisual } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RelatedRecordRowActions } from "@/components/table/RelatedRecordRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { GridCardWithActions } from "./GridCardWithActions";
import { AudiovisualLinkGridCard } from "./relatedLinkGridCards";

interface LinksProps {
  id: string;
  type: LinkableType;
}

/**
 * ArtworkLinks component
 * @returns {JSX.Element}
 */
const AudiovisualLinks = ({ id, type }: LinksProps) => {
  const { t } = useTranslation();

  const [getPaginationRelatedAudiovisuals, setPaginationRelatedAudiovisuals] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedAudiovisual",
    getPaginationRelatedAudiovisuals.pageSize,
    getPaginationRelatedAudiovisuals.page *
      getPaginationRelatedAudiovisuals.pageSize,
  );

  const { data: countRelatedAudiovisuals } = useCountRelatedFields(id, type, [
    "relatedAudiovisual",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedAudiovisual",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedAudiovisual",
  );

  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedAudiovisualId, setSelectedAudiovisualId] = useState<string | null>(
    null,
  );

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const [searchContent, setSearchContent] = useState<string>("");

  const { data: countByStatus } = useCountByStatus();
  const totalSize = countByStatus?.published + countByStatus?.draft;

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const { data: notRelatedAudiovisual } = useGetNotRelatedResource<Audiovisual>(
    {
      resourceId: id,
      resultType: "audiovisual",
      searchQuery: searchContent,
      pagination: getPagination,
    },
  );

  const handleCreateRelation = useCallback(
    (idToLink: string) => {
      createRelatedFields([id, idToLink]);
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteRelation(id);
    },
    [deleteRelation],
  );

  const baseColumns = useMemo(
    () => generateTable(audiovisualColumns, "audiovisual", t),
    [t],
  );

  const renderAudiovisualRowActions = useCallback(
    (item: Audiovisual) => (
      <RelatedRecordRowActions
          isAddMode={isAddMode}
          resourceId={item.id}
          previewType="audiovisual"
          previewElements={[
            {
              label: t("audiovisuelDescriptionNumeroEnregistrement"),
              value: item.audiovisuelDescriptionNumeroEnregistrement,
              type: "text",
            },
            {
              label: t("audiovisuelDescriptionResume"),
              value: item.audiovisuelDescriptionResume,
              type: "richtext",
            },
            {
              label: t("attachments"),
              value: item.attachments,
              type: "image",
            },
          ]}
          onDelete={() => handleDeleteRelation(item.id)}
          onAdd={() => handleCreateRelation(item.id)}
        />
    ),
    [handleCreateRelation, handleDeleteRelation, isAddMode, t],
  );

  const columns: ColumnDef<Audiovisual, any>[] = useMemo(() => {
    return [
      ...baseColumns,
      createActionsColumnDef<Audiovisual>(({ row }) =>
        renderAudiovisualRowActions(row.original),
      ),
    ];
  }, [baseColumns, renderAudiovisualRowActions]);

  const STORAGE_KEY = "audiovisual-links-table";
  const defaultVisibility = {
    ...audiovisualColumnsVisibility,
    actions: true,
  };
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    },
  );
  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const tableOptions = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnsOrder,
    state: { columnVisibility, columnOrder: columnsOrder },
  };

  const relatedPaginationProps = {
    pagination: getPaginationRelatedAudiovisuals,
    setPage: setPaginationRelatedAudiovisuals,
    totalSize: countRelatedAudiovisuals?.relatedAudiovisual ?? 0,
  };

  return (
    <RelatedLinksListGridLayout
      storageKey={STORAGE_KEY}
      title={t("linkedAudiovisuals")}
      isAddMode={isAddMode}
      onBack={handleAddMode}
      onToggleAdd={handleAddMode}
      searchInput={
        <div className="flex items-center gap-2">
          <Input
            placeholder={t("search")}
            className="w-80"
            value={searchContent}
            onChange={(e) => setSearchContent(e.target.value)}
          />
        </div>
      }
      addModeTable={
        <DataTable
          columns={columns}
          data={notRelatedAudiovisual?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: totalSize,
          }}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      listTable={
        <DataTable
          columns={columns}
          data={relatedFields ?? []}
          pagination={relatedPaginationProps}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      gridContent={
        <>
          {relatedFields?.map((row: Audiovisual) => (
            <div key={row.id}>
              <GridCardWithActions actions={renderAudiovisualRowActions(row)}>
                <AudiovisualLinkGridCard
                item={row}
                t={t}
                onTextClick={() => setSelectedAudiovisualId(row.id)}
              />
              </GridCardWithActions>
              <ShowElements
                type="audiovisual"
                id={row.id}
                open={selectedAudiovisualId === row.id}
                onOpenChange={(open) => {
                  if (!open) {
                    setSelectedAudiovisualId(null);
                  }
                }}
                trigger={<span />}
                elements={[
                  {
                    label: t("audiovisuelDescriptionNumeroEnregistrement"),
                    value: row.audiovisuelDescriptionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("audiovisuelDescriptionResume"),
                    value: row.audiovisuelDescriptionResume,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.attachments,
                    type: "image",
                  },
                ]}
              />
            </div>
          ))}
        </>
      }
      relatedPagination={relatedPaginationProps}
    />
  );
};

export default AudiovisualLinks;
